import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { styled } from '@compiled/react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useDebouncedCallback } from 'use-debounce';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import AddIcon from '@atlaskit/icon/core/add';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import CopyIcon from '@atlaskit/icon/core/copy';
import EditIcon from '@atlaskit/icon/core/edit';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import SearchIcon from '@atlaskit/icon/core/search';
import FilledStarIcon from '@atlaskit/icon/core/star-starred';
import UnfilledStarIcon from '@atlaskit/icon/core/star-unstarred';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import VerifiedIcon from '@atlaskit/icon/core/status-verified';
import { RovoIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { type PopupProps, type TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { useRovoAgentCreateAnalytics } from '@atlaskit/rovo-agent-analytics/create';
import { AgentVerificationDropdownItem } from '@atlaskit/rovo-agent-components/agent-verification-dropdown-item';
import { AgentMenuItemSkeleton } from '@atlaskit/rovo-agent-components/ui/AgentMenuItemSkeleton';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import {
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { ChatHeaderMenu } from '@atlassian/conversation-assistant-ui-components/ui/chat-header-menu/ChatHeaderMenu';
import { ChatHeaderMenuList } from '@atlassian/conversation-assistant-ui-components/ui/chat-header-menu/ChatHeaderMenuList';
import { MenuItem } from '@atlassian/conversation-assistant-ui-components/ui/chat-header-menu/MenuItem';
import { LoadingState } from '@atlassian/conversation-assistant-ui-components/ui/LoadingState';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useAgentPermissions } from '../../../controllers/agent-permissions/index.ts';
import { useUserSitePermissions } from '../../../controllers/agent-site-permissions/index.tsx';
import { useAgentUrlActions } from '../../../controllers/agent-url-actions/index.tsx';
import {
	useAgentsPaginatedActionsWithOptimistic,
	usePaginatedAgentsActions,
	usePaginatedAgentsByCategory,
	useRecommendedAgents,
} from '../../../controllers/agents/index.tsx';
import { useInfiniteScroll } from '../../../controllers/infinite-scroll/index.tsx';
import { getDisplayedAgents } from '../../utils/agent-display/index.tsx';
import { getAtlassianStudioNLCreateUrl } from '../../utils/atlassian-studio-url/index.tsx';

import type { agentMenuVerificationDropdownItemQuery } from './__generated__/agentMenuVerificationDropdownItemQuery.graphql.ts';
import type { agentMenuVerificationDropdownItemQuery_activationQuery } from './__generated__/agentMenuVerificationDropdownItemQuery_activationQuery.graphql.ts';
import { AgentMenuItem } from './agent-menu-item/index.tsx';
import messages from './messages.ts';
import { useAgentMenuComboBox } from './use-agent-menu-combobox.ts';

const MAX_AGENT_ITEMS = 6;

export const EMPTY_PAGE_INFO = {
	startCursor: undefined,
	endCursor: undefined,
	hasNextPage: false,
	hasPreviousPage: false,
};

const styles = cssMap({
	agentsSection: {
		width: '100%',
		// Item height (32px) + gaps (2px) in between 6 items
		maxHeight: `${MAX_AGENT_ITEMS * 32 + (MAX_AGENT_ITEMS - 1) * 2}px`,
		overflowY: 'auto',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.025'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	searchInputContainer: {
		display: 'flex',
		paddingTop: token('space.050'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.075'),
		flexDirection: 'column',
		gap: token('space.100'),
		alignSelf: 'stretch',
	},
	loadingStateContainer: {
		display: 'flex',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
	},
	createAgentContainer: {
		width: '100%',
	},
});

// need specific styling for Textfield to match design
// eslint-disable-next-line @compiled/no-styled-tagged-template-expression, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled
const TextfieldInputWrapper = styled.div`
	& > [data-ds--text-field--container] {
		height: 32px;
		border-radius: ${token('radius.large')};
		border: ${token('border.width')} solid ${token('color.border')};
	}
`;

interface AgentMenuProps {
	selectedAgent?: Agent; // undefined means we're in default/Rovo mode
	isAgentDropdownOpen: boolean;
	setIsAgentDropdownOpen: (isOpen: boolean) => void;
	trigger: (triggerProps: TriggerProps) => React.JSX.Element;
	placement: PopupProps['placement'];
	/**
	 * Renders the menu inside the trigger's DOM tree instead of a portal. Set this
	 * when the menu lives inside a surface that dismisses on outside interaction,
	 * such as the editor's inline AI prompt.
	 */
	shouldRenderToParent?: boolean;
	onClose: () => void;
	siteId: string;
	createConversationOptimistically: (options: { sourceAgentId?: string }) => void;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	touchpointSource?: string;
	/** When true, auto-focuses the search text field each time the popup opens. */
	autofocus?: boolean;
	/** Override the default browse agent click handler with custom functionality. */
	onBrowseAgentClick?: () => void;
	/** Agents to pin at the start of the agent list (only shown when not searching). */
	defaultAgents?: Agent[];
	/** Optional callback for handling agent selection instead of createConversationOptimistically. */
	onAgentSelect?: (agent: Agent | undefined) => void;
	/** Called after an agent's favourite status is toggled. Receives the agent with the updated favourite value. */
	onAgentFavouriteToggle?: (updatedAgent: Agent) => void;
	/** Whether to show the browse agents menu item. @default true */
	showBrowseAgents?: boolean;
	/** Whether to show the create agent menu item. @default true */
	showCreateAgent?: boolean;
	/** Whether to show the search agents input field. @default true */
	showSearchAgents?: boolean;
}

export const AgentMenu = ({
	selectedAgent,
	isAgentDropdownOpen,
	setIsAgentDropdownOpen,
	trigger,
	placement,
	shouldRenderToParent,
	onClose,
	siteId,
	createConversationOptimistically,
	assistanceServiceParams,
	touchpointSource = 'agent-menu',
	autofocus: inputAutoFocus,
	onBrowseAgentClick,
	defaultAgents,
	onAgentSelect,
	onAgentFavouriteToggle,
	showBrowseAgents = true,
	showCreateAgent = true,
	showSearchAgents = true,
}: AgentMenuProps): React.JSX.Element => {
	di(
		useUIStoreActions,
		useUserSitePermissions,
		useAgentPermissions,
		useRecommendedAgents,
		usePaginatedAgentsByCategory,
		usePaginatedAgentsActions,
		useAgentsPaginatedActionsWithOptimistic,
	);

	/* eslint-disable-next-line @atlaskit/platform/use-recommended-utils */
	const ntpAgentsDropdownTreatment = FeatureGates.getExperimentValue(
		'rovo_extension_ntp_agents_dropdown',
		'treatment',
		'control',
		{ fireExperimentExposure: false },
	);
	const isComboBoxEnabled = ntpAgentsDropdownTreatment !== 'control';

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [rovoAgentCSID, { trackCreateSessionStart }] = useRovoAgentCreateAnalytics({
		touchPoint: touchpointSource,
		selectedAgentId: selectedAgent?.id,
	});

	const { email } = useProfileController();

	const [inputValue, setInputValue] = useState('');
	const [searchValue, setSearchValue] = useState('');
	// for showing tooltip when agent link is copied
	const [isAgentLinkCopied, setIsAgentLinkCopied] = useState(false);

	useUIStoreSubscription();
	const { setActiveMenu } = useUIStoreActions();
	const { onCopyAgent, onEditAgent, onViewAgentFullProfile } = useAgentUrlActions({
		siteId,
		source: touchpointSource,
	});

	const { isAbleToCreateAgents, isFetchingPermissions } = useUserSitePermissions({
		siteId,
		touchpointSource,
	});

	const { data: agentPermissions, isFetching: isFetchingAgentPermissions } = useAgentPermissions({
		agentId: selectedAgent?.id ?? '',
		enabled: !!selectedAgent,
		assistanceServiceParams,
	});

	const { isFetching, recommendedAgents } = useRecommendedAgents({
		assistanceServiceParams,
		pageUrl: window.location.href,
	});

	const { fetchAgentsByCategory, populateAgentCategories } = usePaginatedAgentsActions({
		assistanceServiceParams,
	});

	const fetchAgentsByCategoryRef = useRef(fetchAgentsByCategory);

	useEffect(() => {
		fetchAgentsByCategoryRef.current = fetchAgentsByCategory;
	}, [fetchAgentsByCategory]);

	const { setFavouriteAgent, setVerifiedAgent } = useAgentsPaginatedActionsWithOptimistic({
		assistanceServiceParams,
	});

	const [
		{
			agentsByCategory: agentsByCategoryAllAgents,
			isCategoryLoading: isCategoryLoadingAllAgents,
			nextPageCursor: nextPageCursorAllAgents,
			hasNextPage: hasNextPageAllAgents,
			agentsError: agentsErrorAllAgents,
		},
	] = usePaginatedAgentsByCategory('all-agents');

	const [
		{
			agentsByCategory: agentsByCategoryFavoriteAgents,
			isCategoryLoading: isCategoryLoadingFavoriteAgents,
			isCategoryInitialized: isCategoryInitializedFavoriteAgents,
		},
	] = usePaginatedAgentsByCategory('starred-agents');

	const isVerifiedAgentsEnabled = fg('rovo-agent-menu-verified-agents');

	const displayedFavoriteAgents = useMemo(() => {
		return agentsByCategoryFavoriteAgents.slice(0, isVerifiedAgentsEnabled ? 3 : 5);
	}, [agentsByCategoryFavoriteAgents, isVerifiedAgentsEnabled]);

	const [
		{
			agentsByCategory: agentsByCategoryVerifiedAgents,
			isCategoryLoading: isCategoryLoadingVerifiedAgents,
			isCategoryInitialized: isCategoryInitializedVerifiedAgents,
		},
	] = usePaginatedAgentsByCategory('verified-agents');

	const displayedVerifiedAgents = useMemo(() => {
		return agentsByCategoryVerifiedAgents.slice(0, 3);
	}, [agentsByCategoryVerifiedAgents]);

	// clear categories when dropdown closes to avoid stale data flash on reopen
	useEffect(() => {
		if (!isAgentDropdownOpen) {
			populateAgentCategories({
				category: 'starred-agents',
				agentsList: [],
				pageInfo: EMPTY_PAGE_INFO,
			});
			populateAgentCategories({
				category: 'all-agents',
				agentsList: [],
				pageInfo: EMPTY_PAGE_INFO,
			});
			populateAgentCategories({
				category: 'verified-agents',
				agentsList: [],
				pageInfo: EMPTY_PAGE_INFO,
			});
		}
	}, [isAgentDropdownOpen, populateAgentCategories]);

	useEffect(() => {
		if (!isAgentDropdownOpen) {
			return;
		}
		if (!searchValue) {
			fetchAgentsByCategoryRef.current({ category: 'starred-agents', query: '' });
			if (isVerifiedAgentsEnabled) {
				fetchAgentsByCategoryRef.current({ category: 'verified-agents', query: '' });
			}
		}
		fetchAgentsByCategoryRef.current({ category: 'all-agents', query: searchValue });
	}, [isAgentDropdownOpen, searchValue, isVerifiedAgentsEnabled]);

	const handleFavouriteAgent = useCallback(
		async (targetAgent: Agent | undefined, source: 'agent-menu-item' | 'base-menu-item') => {
			if (!targetAgent) {
				return;
			}

			const isFavorited = !targetAgent.favourite;

			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'browseAgentsFavoriteAgentButton',
				attributes: {
					interactionSource: touchpointSource,
					menuItemType: source,
					isFavorited,
					agentId: targetAgent.id,
				},
			});

			onAgentFavouriteToggle?.({ ...targetAgent, favourite: isFavorited });

			await setFavouriteAgent(targetAgent.id, isFavorited);
		},
		[setFavouriteAgent, sendAnalyticsEvent, touchpointSource, onAgentFavouriteToggle],
	);

	/**
	 * Function that updates the verified status of an agent.
	 * This can be removed once the dropdown has converted over to relay.
	 */
	const handleVerifyAgent = useCallback(
		(targetAgent: Agent, verified: boolean) => {
			setVerifiedAgent(targetAgent.id, verified);
		},
		[setVerifiedAgent],
	);

	const handleLoadMore = useCallback(async () => {
		// Don't load more if already loading, no next page, or there's an error
		if (isCategoryLoadingAllAgents || !hasNextPageAllAgents || agentsErrorAllAgents) {
			return;
		}

		await fetchAgentsByCategory({
			category: 'all-agents',
			query: searchValue,
			after: nextPageCursorAllAgents,
		});
	}, [
		searchValue,
		fetchAgentsByCategory,
		isCategoryLoadingAllAgents,
		hasNextPageAllAgents,
		nextPageCursorAllAgents,
		agentsErrorAllAgents,
	]);

	const loadMoreRefCallback = useInfiniteScroll<HTMLDivElement>({
		onScrollTo: () => {
			handleLoadMore();
		},
	});

	const [debouncedSearch] = useDebouncedCallback((value: string) => {
		setSearchValue(value);
		sendAnalyticsEvent({
			action: 'searched',
			actionSubject: 'input',
			actionSubjectId: 'browseAgentsSearchInput',
			attributes: {
				interactionSource: touchpointSource,
				searchValueLength: value.length,
			},
		});
		setComboBoxFocused(-1);
	}, 800);

	const handleCopy = useCallback(
		(agent: Agent) => {
			onCopyAgent({
				agentId: agent.id,
				agentIdentityAccountId: agent.identity_account_id,
			});

			setIsAgentLinkCopied(true);
			setTimeout(() => {
				setIsAgentLinkCopied(false);
			}, 2000);
		},
		[onCopyAgent],
	);

	const handleCreateAgent = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'browseAgentsCreateAgentButton',
			attributes: {
				interactionSource: touchpointSource,
			},
		});
		fg('rovo-agents-universal-analytics') && trackCreateSessionStart();
		window.open(
			getAtlassianStudioNLCreateUrl(siteId, touchpointSource, rovoAgentCSID, email),
			'_blank',
		);
	}, [siteId, sendAnalyticsEvent, touchpointSource, trackCreateSessionStart, rovoAgentCSID, email]);

	const handleSelectAgent = useCallback(
		(agent: Agent | undefined) => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'browseAgentsSelectAgentOption',
				attributes: {
					interactionSource: touchpointSource,
					agentId: agent?.id,
					isDefaultAgent: !agent, // true indicates 'Chat with Rovo' was clicked
				},
			});
			if (onAgentSelect) {
				onAgentSelect(agent);
			} else {
				createConversationOptimistically({
					sourceAgentId: agent?.id,
				});
			}
			setIsAgentDropdownOpen(false);
		},
		[
			createConversationOptimistically,
			onAgentSelect,
			setIsAgentDropdownOpen,
			sendAnalyticsEvent,
			touchpointSource,
		],
	);

	const handleBrowseAgentClicked = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'browseAgentsNavigate',
			attributes: {
				interactionSource: touchpointSource,
			},
		});
		if (onBrowseAgentClick) {
			onBrowseAgentClick();
		} else {
			setActiveMenu('agents');
		}
	}, [setActiveMenu, onBrowseAgentClick, sendAnalyticsEvent, touchpointSource]);

	const displayedAgents = useMemo(() => {
		// active search, show favorites + all (no recommended, no defaultAgents)
		if (searchValue) {
			return getDisplayedAgents({
				recommendedAgents: [],
				agentsByCategoryAllAgents,
				agentsByCategoryFavoriteAgents: [],
				agentsByCategoryVerifiedAgents: [],
				selectedAgent,
			});
		}

		// empty search, show default agents + favorite agents + verified agents + all agents + recommended agents
		if (
			!isCategoryInitializedFavoriteAgents ||
			(isVerifiedAgentsEnabled && !isCategoryInitializedVerifiedAgents)
		) {
			// wait for favorites and verified agents to be initialized so we don't have visual layout shift
			return [];
		}

		return getDisplayedAgents({
			recommendedAgents,
			agentsByCategoryAllAgents,
			agentsByCategoryFavoriteAgents: displayedFavoriteAgents,
			agentsByCategoryVerifiedAgents: isVerifiedAgentsEnabled ? displayedVerifiedAgents : [],
			selectedAgent,
			defaultAgents,
		});
	}, [
		displayedFavoriteAgents,
		displayedVerifiedAgents,
		recommendedAgents,
		agentsByCategoryAllAgents,
		selectedAgent,
		searchValue,
		isCategoryInitializedFavoriteAgents,
		defaultAgents,
		isCategoryInitializedVerifiedAgents,
		isVerifiedAgentsEnabled,
	]);

	const {
		inputProps: comboBoxInputProps,
		listBoxProps: comboBoxListBoxProps,
		getOptionProps: getComboBoxOptionProps,
		focusedIndex: comboBoxFocusedIndex,
		setFocused: setComboBoxFocused,
	} = useAgentMenuComboBox({
		items: displayedAgents,
		isOpen: isAgentDropdownOpen,
		onSelect: handleSelectAgent,
		onClose,
		enabled: isComboBoxEnabled,
	});

	const textFieldComponent = (
		<Box xcss={styles.searchInputContainer}>
			<TextfieldInputWrapper>
				<Textfield
					value={inputValue}
					onChange={(e) => {
						const newValue = e.currentTarget.value;
						setInputValue(newValue);
						debouncedSearch(newValue);
					}}
					placeholder={formatMessage(messages.searchPlaceholder)}
					aria-label={formatMessage(messages.searchPlaceholder)}
					elemBeforeInput={<SearchIcon spacing="spacious" color={token('color.icon')} label="" />}
					autoFocus={inputAutoFocus}
					{...comboBoxInputProps}
				/>
			</TextfieldInputWrapper>
		</Box>
	);

	const agentsComponent = (
		<Box xcss={styles.agentsSection} as="ul" {...comboBoxListBoxProps}>
			{displayedAgents.map((agent, index) => (
				<Box as="li" key={agent.id}>
					<AgentMenuItem
						agent={agent}
						renderImmediately={index < MAX_AGENT_ITEMS}
						isSelected={selectedAgent ? agent.id === selectedAgent.id : false}
						onSelect={handleSelectAgent}
						onFavoriteToggle={(agent) => handleFavouriteAgent(agent, 'agent-menu-item')}
						comboBoxOptionProps={getComboBoxOptionProps(index)}
						isFocused={isComboBoxEnabled ? comboBoxFocusedIndex === index : undefined}
					/>
				</Box>
			))}
			{(isFetching ||
				isCategoryLoadingAllAgents ||
				isCategoryLoadingFavoriteAgents ||
				(isVerifiedAgentsEnabled && isCategoryLoadingVerifiedAgents)) &&
				Array(Math.max(0, MAX_AGENT_ITEMS - displayedAgents.length))
					.fill(null)
					.map((_, index) => (
						<Box as="li" key={index}>
							<AgentMenuItemSkeleton index={index} />
						</Box>
					))}
			{hasNextPageAllAgents && !agentsErrorAllAgents && (
				<Box as="li">
					<Box ref={loadMoreRefCallback} xcss={styles.loadingStateContainer}>
						{isCategoryLoadingAllAgents && <LoadingState size="xsmall" />}
					</Box>
				</Box>
			)}
		</Box>
	);

	const createAgentComponent = (
		<Box xcss={styles.createAgentContainer}>
			<Tooltip
				content={formatMessage(messages.createButtonDisabledTooltip)}
				canAppear={() => !isAbleToCreateAgents && !isFetchingPermissions}
			>
				<MenuItem
					label={formatMessage(messages.createAgent)}
					icon={<AddIcon label="" color={token('color.icon.subtle')} />}
					onClick={handleCreateAgent}
					isLoading={isFetchingPermissions}
					isDisabled={isFetchingPermissions || !isAbleToCreateAgents}
					isExternalLink
				/>
			</Tooltip>
		</Box>
	);

	const sections = [
		{
			items: selectedAgent
				? [
						// Hide "Chat with Rovo" when the default Rovo agent (is_default: true)
						// is already present in the defaultAgents list
						...(defaultAgents?.some((agent) => agent.is_default)
							? []
							: [
									<MenuItem
										label={formatMessage(messages.chatWithRovo)}
										icon={<RovoIcon size="xxsmall" />}
										onClick={() => handleSelectAgent(undefined)}
									/>,
								]),
						<MenuItem
							label={formatMessage(messages.viewAgentProfile)}
							icon={<InformationCircleIcon label="" />}
							onClick={() => onViewAgentFullProfile(selectedAgent.id)}
						/>,
						...(!isFetchingAgentPermissions && agentPermissions?.permissions.AGENT_UPDATE?.permitted
							? [
									<MenuItem
										label={formatMessage(messages.editAgent)}
										icon={<EditIcon label="" />}
										onClick={() => onEditAgent(selectedAgent.id)}
									/>,
								]
							: []),
						<MenuItem
							label={
								isAgentLinkCopied
									? formatMessage(messages.copyAgentLinkClickedTooltip)
									: formatMessage(messages.copyLink)
							}
							icon={
								isAgentLinkCopied ? (
									<StatusSuccessIcon color={token('color.icon.success')} label="" />
								) : (
									<CopyIcon label="" />
								)
							}
							onClick={() => handleCopy(selectedAgent)}
						/>,
						<MenuItem
							label={formatMessage(messages.favoriteAgent)}
							icon={
								selectedAgent.favourite ? (
									<FilledStarIcon color={token('color.icon.warning')} label="" />
								) : (
									<UnfilledStarIcon label="" />
								)
							}
							onClick={() => handleFavouriteAgent(selectedAgent, 'base-menu-item')}
						/>,
						...(fg('rovo_agents_agent_verification')
							? [
									<AtlassianRelayEnvironmentProvider>
										<React.Suspense fallback={null}>
											<AgentMenuVerificationDropdownItem
												agentId={selectedAgent.id}
												agentAri={selectedAgent.agentAri}
												cloudId={assistanceServiceParams.siteId}
												handleVerifyAgent={(verified) => handleVerifyAgent(selectedAgent, verified)}
											/>
										</React.Suspense>
									</AtlassianRelayEnvironmentProvider>,
								]
							: []),
					]
				: [],
		},
		...(showSearchAgents
			? [
					{
						title: formatMessage(messages.switchAgent),
						items: [textFieldComponent, agentsComponent],
					},
				]
			: []),
		{
			items: [
				...(showBrowseAgents
					? [
							<MenuItem
								label={formatMessage(messages.browseAgents)}
								icon={<AiAgentIcon label="" color={token('color.icon.subtle')} />}
								onClick={handleBrowseAgentClicked}
							/>,
						]
					: []),
				...(showCreateAgent ? [createAgentComponent] : []),
			].filter(Boolean),
		},
	].filter((section) => section.items.length > 0);

	return (
		<ChatHeaderMenu
			isOpen={isAgentDropdownOpen}
			placement={placement}
			width="255px"
			maxWidth="255px"
			onClose={onClose}
			trigger={trigger}
			sections={sections}
			content={<ChatHeaderMenuList sections={sections} />}
			popoverAutoFocus={inputAutoFocus ? false : undefined}
			shouldRenderToParent={shouldRenderToParent}
		/>
	);
};

type AgentMenuVerificationDropdownItemProps = {
	/** Non-ARI Agent ID. Typically comes from the REST API or has been derived from the ARI. */
	agentId: string;
	/** Agent ARI ID. If provided, skips the activation query and uses this directly. */
	agentAri?: string | null;
	/** Atlassian cloud ID for the site (also known as 'siteId'). */
	cloudId: string;
	/** Callback fired when verification status changes successfully. */
	handleVerifyAgent: (verified: boolean) => void;
};

const AgentMenuVerificationDropdownItem = ({
	agentId,
	agentAri: agentAriProp,
	cloudId,
	handleVerifyAgent,
}: AgentMenuVerificationDropdownItemProps): React.JSX.Element | null => {
	// TODO: Replace this activation query + ARI construction with useResolveAgentAri from controllers/agent-ari
	// Step 1: Get activation ID if we don't have agentAri
	const activationResult = useLazyLoadQuery<agentMenuVerificationDropdownItemQuery_activationQuery>(
		graphql`
			query agentMenuVerificationDropdownItemQuery_activationQuery(
				$cloudId: ID!
				$product: String!
				$skip: Boolean!
			) {
				tenantContexts(cloudIds: [$cloudId]) @skip(if: $skip) {
					activationIdByProduct(product: $product) {
						active
					}
				}
			}
		`,
		{
			cloudId,
			product: 'rovo',
			skip: Boolean(agentAriProp), // skip if we already have agentAri
		},
	);

	// Step 2: Construct agentAri from activation ID + agentId, or use provided agentAri
	const agentAri = useMemo(() => {
		if (agentAriProp) {
			return agentAriProp;
		}
		const activationId = activationResult?.tenantContexts?.[0]?.activationIdByProduct?.active;
		return activationId ? `ari:cloud:rovo::agent/activation/${activationId}/${agentId}` : null;
	}, [agentAriProp, activationResult, agentId]);

	// Step 3: Fetch agent data using the agentAri
	const data = useLazyLoadQuery<agentMenuVerificationDropdownItemQuery>(
		graphql`
			query agentMenuVerificationDropdownItemQuery($agentAri: ID!, $cloudId: ID!, $skip: Boolean!) {
				agentStudio_agentById(id: $agentAri) @optIn(to: "AgentStudio") @skip(if: $skip) {
					...agentVerificationDropdownItem_AtlaskitRovoAgentComponents_agentRef
						@dangerously_unaliased_fixme
				}
				atlassianStudio_userSiteContext(cloudId: $cloudId) @skip(if: $skip) {
					... on AtlassianStudioUserSiteContextOutput {
						userPermissions {
							...agentVerificationDropdownItem_AtlaskitRovoAgentComponents_userPermissionsRef
						}
					}
				}
			}
		`,
		{
			agentAri: agentAri ?? '',
			cloudId,
			skip: !agentAri, // skip if we couldn't construct agentAri
		},
	);

	const agentRef = data.agentStudio_agentById;
	const userPermissionsRef = data.atlassianStudio_userSiteContext?.userPermissions;

	if (!agentRef || !userPermissionsRef) {
		return null;
	}

	return (
		<AgentVerificationDropdownItem
			agentRef={agentRef}
			userPermissionsRef={userPermissionsRef}
			onVerificationSuccess={(verified) => handleVerifyAgent(verified)}
			renderItem={({ labelText, isVerified, onClick, isPending }) => (
				<MenuItem
					label={labelText}
					// When the mutation is pending, don't allow clicking the item
					onClick={isPending ? () => null : onClick}
					isLoading={isPending}
					icon={
						<VerifiedIcon
							color={isVerified ? token('color.icon.accent.blue') : undefined}
							label=""
						/>
					}
				/>
			)}
		/>
	);
};
